import { useState, useMemo, useCallback } from 'react';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Checkbox } from '../../components/ui/checkbox';
import { Accordion } from '../../components/common/Accordion';
import { EstimateCard } from '../../components/common/EstimateCard';
import { StepHeader } from '../../components/common/StepHeader';
import { AdditionalDetails } from '../../components/wizard/AdditionalDetails';
import { CustomServiceRequest } from '../../components/wizard/CustomServiceRequest';
import { WizardNavigation } from '../../components/wizard/WizardNavigation';
import { useWizardFlow } from '../../hooks/useWizardFlow';
import {
  ELECTRICAL_QUESTIONS,
  ELECTRICAL_SERVICE_CATEGORIES
} from './electricalData';
import { calculateElectricalEstimate } from './electricalPricing';
import { shouldShowQuestion } from './electricalValidation';
import { MAX_ADDITIONAL_DETAILS } from '../../data/constants';

/**
 * ElectricalFlow
 *
 * Modular Electrical wizard flow. Structure mirrors PlumbingFlow so that
 * all shared behaviour (useWizardFlow, EstimateCard, CustomServiceRequest,
 * AdditionalDetails, WizardNavigation) is reused rather than duplicated.
 */
export const ElectricalFlow = ({
  answers,
  setAnswers,
  additionalDetails,
  setAdditionalDetails,
  unlistedServiceDescription = '',
  setUnlistedServiceDescription = () => {},
  onComplete,
  onBack
}) => {
  const [expandedCategories, setExpandedCategories] = useState([]);

  const selectedServices = answers.electrical_services || [];

  const {
    currentQuestion,
    currentQuestionIndex,
    isLastQuestion,
    canProceed: hookCanProceed,
    goNext,
    goBack,
    handleRadioAnswer,
    handleCheckboxChange
  } = useWizardFlow({
    questions: ELECTRICAL_QUESTIONS,
    answers,
    setAnswers,
    onComplete: () => {
      const finalAnswers = {
        ...answers,
        ...(additionalDetails.trim() && { additional_details: additionalDetails.trim() })
      };
      const estimatedPrice = calculateElectricalEstimate(selectedServices, answers);
      onComplete(finalAnswers, estimatedPrice);
    },
    onBack,
    shouldShowQuestion,
    autoAdvanceOnRadio: true,
    autoAdvanceDelay: 300,
    initialQuestionIndex: 0
  });

  const estimatedPrice = useMemo(
    () => calculateElectricalEstimate(selectedServices, answers),
    [selectedServices, answers]
  );

  const toggleCategory = useCallback((categoryId) => {
    setExpandedCategories((prev) =>
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId]
    );
  }, []);

  const handleServiceToggle = useCallback(
    (serviceValue, checked) => {
      const current = answers.electrical_services || [];
      const newServices = checked
        ? [...current, serviceValue]
        : current.filter((v) => v !== serviceValue);
      setAnswers((prev) => ({ ...prev, electrical_services: newServices }));
    },
    [answers, setAnswers]
  );

  const handleClearServices = useCallback(() => {
    setAnswers((prev) => ({ ...prev, electrical_services: [] }));
  }, [setAnswers]);

  // For the service-selection screen, allow proceeding when either a service
  // is picked OR the customer only filled in the "Can't find" description.
  const canProceed = useMemo(() => {
    if (!currentQuestion) return false;
    if (currentQuestion.type === 'collapsible_categories') {
      return selectedServices.length > 0 || !!unlistedServiceDescription.trim();
    }
    return hookCanProceed;
  }, [currentQuestion, selectedServices, hookCanProceed, unlistedServiceDescription]);

  const handleBack = useCallback(() => {
    if (currentQuestionIndex > 0) {
      goBack();
    } else {
      onBack();
    }
  }, [currentQuestionIndex, goBack, onBack]);

  if (!currentQuestion) {
    return <div>Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <StepHeader
        title={currentQuestion.question}
        subtitle={currentQuestion.subtitle}
        onBack={handleBack}
        showBack={true}
      />

      <div className="space-y-4">
        {/* Service selection */}
        {currentQuestion.type === 'collapsible_categories' && (
          <>
            <div className="space-y-3">
              {ELECTRICAL_SERVICE_CATEGORIES.map((category) => {
                const isExpanded = expandedCategories.includes(category.id);
                const selectedInCategory = category.services.filter((s) =>
                  selectedServices.includes(s.value)
                ).length;

                return (
                  <Accordion
                    key={category.id}
                    id={category.id}
                    title={category.name}
                    emoji={category.emoji}
                    badge={selectedInCategory > 0 ? `${selectedInCategory} selected` : null}
                    isExpanded={isExpanded}
                    onToggle={() => toggleCategory(category.id)}
                    testIdPrefix="category"
                  >
                    {category.services.map((service) => {
                      const isChecked = selectedServices.includes(service.value);
                      return (
                        <label
                          key={service.value}
                          className={`flex items-center gap-4 p-4 pl-12 cursor-pointer border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors ${
                            isChecked ? 'bg-[#0FA3B1]/5' : ''
                          }`}
                          data-testid={`service-${service.value}`}
                        >
                          <Checkbox
                            checked={isChecked}
                            onCheckedChange={(checked) => handleServiceToggle(service.value, checked)}
                            className="border-2 border-gray-300 data-[state=checked]:bg-[#0FA3B1] data-[state=checked]:border-[#0FA3B1]"
                          />
                          <div className="flex-1">
                            <span className="text-[#1F2933]">{service.label}</span>
                            {service.price && (
                              <span className="text-xs text-[#1F2933]/50 ml-2">
                                ${service.price.low}–${service.price.high}
                              </span>
                            )}
                          </div>
                        </label>
                      );
                    })}
                  </Accordion>
                );
              })}
            </div>

            <div className="flex items-center justify-between pt-2">
              <span className="text-sm text-[#1F2933]/60">
                {selectedServices.length} service(s) selected
              </span>
              {selectedServices.length > 0 && (
                <button
                  type="button"
                  onClick={handleClearServices}
                  className="text-sm text-red-500 hover:text-red-600"
                  data-testid="electrical-clear-services"
                >
                  Clear all
                </button>
              )}
            </div>
          </>
        )}

        {/* Radio */}
        {currentQuestion.type === 'radio' && (
          <RadioGroup
            value={answers[currentQuestion.id] || ''}
            onValueChange={handleRadioAnswer}
            className="space-y-3"
          >
            {currentQuestion.options.map((option) => (
              <label
                key={option.value}
                className={`flex items-center gap-4 p-4 bg-gray-50 border rounded-xl cursor-pointer transition-all hover:border-[#0FA3B1]/50 ${
                  answers[currentQuestion.id] === option.value
                    ? 'border-[#0FA3B1] bg-[#0FA3B1]/5'
                    : 'border-gray-200'
                }`}
                data-testid={`option-${option.value}`}
              >
                <RadioGroupItem
                  value={option.value}
                  className="border-2 border-gray-300 text-[#0FA3B1]"
                />
                <span className="text-[#1F2933] flex-1">{option.label}</span>
              </label>
            ))}
          </RadioGroup>
        )}

        {/* Checkbox */}
        {currentQuestion.type === 'checkbox' && (
          <div className="space-y-3">
            {currentQuestion.options.map((option) => {
              const isChecked = (answers[currentQuestion.id] || []).includes(option.value);
              return (
                <label
                  key={option.value}
                  className={`flex items-center gap-4 p-4 bg-gray-50 border rounded-xl cursor-pointer transition-all hover:border-[#0FA3B1]/50 ${
                    isChecked ? 'border-[#0FA3B1] bg-[#0FA3B1]/5' : 'border-gray-200'
                  }`}
                  data-testid={`option-${option.value}`}
                >
                  <Checkbox
                    checked={isChecked}
                    onCheckedChange={(checked) => handleCheckboxChange(option.value, checked)}
                    className="border-2 border-gray-300 data-[state=checked]:bg-[#0FA3B1] data-[state=checked]:border-[#0FA3B1]"
                  />
                  <span className="text-[#1F2933] flex-1">{option.label}</span>
                </label>
              );
            })}
          </div>
        )}
      </div>

      {/* Additional Details */}
      <div className="mt-6">
        <AdditionalDetails
          value={additionalDetails}
          onChange={setAdditionalDetails}
          placeholder="Share any specific requirements, measurements, preferences, or unique project details..."
          maxLength={MAX_ADDITIONAL_DETAILS}
          minHeight="100px"
        />
      </div>

      {/* "Can't find what you need?" — only on first (service selection) step */}
      {currentQuestionIndex === 0 && (
        <CustomServiceRequest
          value={unlistedServiceDescription}
          onChange={setUnlistedServiceDescription}
        />
      )}

      {/* Running Estimate */}
      <EstimateCard
        priceRange={estimatedPrice}
        isCustomRequest={!estimatedPrice.hasValidPrice && !!unlistedServiceDescription.trim()}
      />

      {/* Bottom Back + Continue */}
      <WizardNavigation
        onBack={handleBack}
        onContinue={goNext}
        continueDisabled={!canProceed}
        continueLabel={isLastQuestion ? 'Get My Quote' : 'Continue'}
        continueTestId={isLastQuestion ? 'electrical-complete' : 'electrical-next'}
      />
    </div>
  );
};

export default ElectricalFlow;
